import isValidUrlOrRelativeUrl from '@honda-canada/js-utilities/lib/isValidUrlOrRelativeUrl';
import React, { useContext, useEffect } from 'react';
import { Box, Media } from '@honda-canada/design-system-react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import BottomElementContext from '../Footer/service/BottomElementProvider/BottomElementContext';
import Desktop from './Desktop';
import Mobile from './Mobile';
import themeStyles from './modelPageNav.styles';
import useSharedApps from '../../utils/sitecoreContext/useSharedApps';
import { BUILD_AND_PRICE_CONFIGURE_URL, OFFERS_URL } from '../../utils/constants';
import { ModelExplorationContext } from '../ModelExplorationContext';
import {
  SIDENAV_LAYOUT_COMPONENT_NAME,
  TITLE_COMPONENT_NAME,
  TRIM_PACKAGES_COMPONENT_NAME,
  useConfiguration,
} from './constants';
import { Screen } from '../../utils/components/ForPrint.styles';
import { getComponentFromRoute } from '../../utils/sitecorePlaceholders';
import { getModelNavPageBodyStyle, useZindexWorkaround } from './utils';

const ModelPageNavContainer = themeStyles.apply(Box, 'ModelPageNavContainer');

const getBuildButtonStyling = (isDark, isTrimSpecial) => (isDark || isTrimSpecial ? 'special' : 'primary');

const ModelPageNav = ({
  modelPageSettings,
  vehicleType,
  navItems,
  rendering,
  dropdownOptions,
  categoryName,
  modelName,
  desktopStickyTopPosition,
  mobileStickyTopPosition,
  variant,
  gtm,
  getSelectedOptionItem,
}) => {
  const modelExplorationContext = useContext(ModelExplorationContext) || {};
  const { bapUrlParams, setIsModelNavOpen, modelYear, subCategory, offersUrlParams, selectedTrim } =
    modelExplorationContext;
  const bodyStyle = getModelNavPageBodyStyle(variant, categoryName, vehicleType, modelYear);
  const selectedOptionItem = getSelectedOptionItem(dropdownOptions, subCategory, modelYear);
  const { setIsHidden, setHeaderHiddenOnScroll, setDealerBannerHiddenOnScroll, setHeaderUnderModelNav } =
    useContext(BottomElementContext);
  const { sitecoreContext } = useSitecoreContext();
  const isProgressSideNav = !!getComponentFromRoute(SIDENAV_LAYOUT_COMPONENT_NAME, sitecoreContext?.route);
  const isTrimPackages = !!getComponentFromRoute(TRIM_PACKAGES_COMPONENT_NAME, sitecoreContext?.route);
  const hasTitleComponent = !!getComponentFromRoute(TITLE_COMPONENT_NAME, sitecoreContext?.route);
  const bapPathUrl = useSharedApps(BUILD_AND_PRICE_CONFIGURE_URL);
  const offersBaseUrl = useSharedApps(OFFERS_URL);
  const zIndexWorkaround = useZindexWorkaround();
  const { zIndexWorkaroundClassName, zIndexStringValue, showModelNameBadge } = useConfiguration(variant);

  useEffect(() => {
    if (typeof setDealerBannerHiddenOnScroll !== 'function' || typeof setHeaderHiddenOnScroll !== 'function') return;

    if (isProgressSideNav) {
      setHeaderHiddenOnScroll(false);
      setDealerBannerHiddenOnScroll(false);
    } else {
      setHeaderHiddenOnScroll(true);
      setDealerBannerHiddenOnScroll(true);
    }
    if (isTrimPackages && hasTitleComponent) {
      setHeaderHiddenOnScroll(false);
      setDealerBannerHiddenOnScroll(false);
      setHeaderUnderModelNav(true);
    } else {
      setHeaderUnderModelNav(false);
    }
    return () => {
      // Clean-up on component dismount
      setIsHidden(false);
      if (typeof setHeaderHiddenOnScroll === 'function') setHeaderHiddenOnScroll(false);
      if (typeof setDealerBannerHiddenOnScroll === 'function') setDealerBannerHiddenOnScroll(false);
    };
  }, [isProgressSideNav]);

  const { pageEditing } = sitecoreContext;

  if (!modelPageSettings) return null;

  const isDark = modelPageSettings?.isDarkMode?.value === '1';

  const modelBadge = modelPageSettings?.nameBadge?.item?.value;
  const specialTrimPage = (navItems && navItems?.find(navItem => navItem.nameBadge?.item?.value?.src)) || '';
  const ctaLink = isValidUrlOrRelativeUrl(modelPageSettings?.ctaLink?.item?.value?.href)
    ? modelPageSettings?.ctaLink?.item?.value
    : undefined;
  const showCTA = !!ctaLink?.href;
  const isNsx = modelPageSettings?.isNsx?.value;
  const bapBaseUrl = isNsx ? ctaLink?.href : bapPathUrl;
  const bapLabel = ctaLink?.text;
  const props = {
    showCTA,
    modelName,
    specialTrimPage,
    modelBadge,
    navItems,
    bapBaseUrl,
    bapLabel,
    offersBaseUrl,
    gtm,
    getSelectedOptionItem,
    showModelNameBadge,
  };
  return (
    <>
      {zIndexWorkaround}
      <ModelPageNavContainer
        data-gtm-category="model interactions"
        data-gtm-component-type={rendering?.componentName}
        className={zIndexWorkaroundClassName}
        zIndex={pageEditing ? 210 : zIndexStringValue}
        backgroundColor={isDark ? 'black' : 'white'}
        position="sticky"
        top={isProgressSideNav ? [mobileStickyTopPosition, mobileStickyTopPosition, desktopStickyTopPosition] : 0}
      >
        <Screen>
          <Media lessThan="desktop">
            <Mobile
              {...props}
              bapUrlParams={isNsx ? '' : bapUrlParams}
              offersUrlParams={offersUrlParams}
              setIsModelNavOpen={setIsModelNavOpen}
              options={dropdownOptions}
              selectedOption={selectedOptionItem}
              isDark={isDark}
              bodyStyle={bodyStyle}
              buildButtonStyling={getBuildButtonStyling(isDark, selectedTrim?.isSpecialType)}
              variant={variant}
              vehicleType={vehicleType}
              selectedTrim={selectedTrim}
              showModelNameBadge={showModelNameBadge}
              rendering={rendering}
            />
          </Media>
          <Media greaterThanOrEqual="desktop">
            <Desktop
              {...props}
              bapUrlParams={isNsx ? '' : bapUrlParams}
              isDark={isDark}
              offersUrlParams={offersUrlParams}
              options={dropdownOptions}
              selectedOption={selectedOptionItem}
              bodyStyle={bodyStyle}
              buildButtonStyling={getBuildButtonStyling(isDark, selectedTrim?.isSpecialType)}
              variant={variant}
              vehicleType={vehicleType}
              selectedTrim={selectedTrim}
              showModelNameBadge={showModelNameBadge}
              rendering={rendering}
            />
          </Media>
        </Screen>
      </ModelPageNavContainer>
    </>
  );
};

export default ModelPageNav;
